import { GridItem, HStack, Text, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { useUpdateTaskPriorityMutation } from "@src/__generated__/urql-graphql";
import type {
  InternalTaskType,
  ScopedTaskType,
} from "@src/components/modules/resource-planning/tasks/listing/store";
import { ExternalAccessIcon } from "@src/components/widgets/ExternalAccessIcon";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { Fragment } from "react";
import { TaskPriorityBadge } from "../TaskPriorityBadge";
import { TaskItemBase, type TaskItemBaseProps } from "./TaskItemBase";
import {
  TaskAvatars,
  TaskDeadline,
  TaskPlannedBudgetedTime,
} from "./TaskItemComponents";

type TTaskItemListingProps<TTask extends InternalTaskType | ScopedTaskType> =
  Omit<TaskItemBaseProps<TTask>, "content" | "title">;

export const taskItemGridTemplateColumns = {
  base: "9rem 5rem minmax(12rem, 1fr) 15% 10% 10rem 13.5rem 8rem 6.5rem 5.5rem",
  "2xl":
    // eslint-disable-next-line lingui/no-unlocalized-strings
    "11rem 5rem minmax(12rem, 1fr) 15% 10% 10rem 13.5rem 8rem 6.5rem 5.5rem",
};

export const TaskItemListing = observer(function TaskItemListing<
  TTask extends InternalTaskType | ScopedTaskType,
>(props: TTaskItemListingProps<TTask>) {
  return (
    <TaskItemBase
      title={props.task?.name ?? ""}
      subtasksVisible={props?.subtasksVisible}
      onCreateSubtask={props?.onCreateSubtask}
      wrapperStyle={{
        gridTemplateColumns: taskItemGridTemplateColumns,
      }}
      content={(task: TTask) => <TaskItemListingContent task={task} />}
      {...props}
    />
  );
});

type TaskItemListingContentProps = {
  task: InternalTaskType | ScopedTaskType;
};
const TaskItemListingContent = observer(function TaskItemListingContent({
  task,
}: TaskItemListingContentProps) {
  const { workspaceStore, authStore } = useStore();
  const [{ fetching: updatingPriority }, updatePriority] =
    useUpdateTaskPriorityMutation();

  const projectId = task.ourWorkBudgetItem?.project.id;
  const projectTitle = task.ourWorkBudgetItem?.project.title;

  return (
    <Fragment>
      <Tooltip
        label={
          task.ourWorkBudgetItem
            ? `${task.ourWorkBudgetItem.project.brand.client.name} - ${task.ourWorkBudgetItem.project.brand.name} - ${projectTitle}`
            : t`Unassigned`
        }
      >
        <Text textOverflow="ellipsis" noOfLines={1}>
          {task.ourWorkBudgetItem?.project.code ? (
            <Text>
              {task.ourWorkBudgetItem?.project.code}&nbsp;{projectTitle}
            </Text>
          ) : (
            <Trans>Unassigned</Trans>
          )}
        </Text>
      </Tooltip>
      {authStore.isInternalUser ? (
        <Tooltip
          label={
            task.ourWorkBudgetItem && "title" in task.ourWorkBudgetItem
              ? task.ourWorkBudgetItem.title
              : t`Unassigned`
          }
        >
          <Text textOverflow="ellipsis" noOfLines={1}>
            {task.ourWorkBudgetItem &&
            "title" in task.ourWorkBudgetItem &&
            task.ourWorkBudgetItem.title ? (
              <Text>{task.ourWorkBudgetItem.title}</Text>
            ) : (
              <Trans>Unassigned</Trans>
            )}
          </Text>
        </Tooltip>
      ) : (
        <GridItem />
      )}
      {authStore.isInternalUser ? (
        <TaskDeadline deadline={task.deadline} />
      ) : (
        <GridItem />
      )}
      {"stats" in task ? (
        <HStack justify="space-between">
          <TaskPlannedBudgetedTime
            plannedTime={task.stats?.planned_time ?? 0}
            spentTime={task.stats?.spent_time ?? 0}
            projectId={projectId}
            budgetItemId={task.ourWorkBudgetItem?.id}
            taskId={task.id}
            parentTaskId={task.parent?.id}
            usesBudgetovac={task.ourWorkBudgetItem?.project.uses_budgetovac}
          />
          <ExternalAccessIcon
            type="task"
            id={task.id}
            scopes={task.scopes}
            isTemplate={task.is_template}
          />
        </HStack>
      ) : (
        <GridItem />
      )}
      <TaskPriorityBadge
        priority={task.priority}
        onChange={async (newPriorityId) => {
          const { data, error } = await updatePriority({
            task_id: task.id,
            priority_id: newPriorityId,
          });

          if (!data || error) return;
          task.priority = data.updateTaskPriority.priority;
        }}
        my="auto!"
        minW="80px"
        isEditable
        taskPriorities={workspaceStore.taskPriorities}
        isLoading={updatingPriority}
      />
      <TaskAvatars positions={task.positions} />
    </Fragment>
  );
});
